import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, ShieldCheck, Loader2, Lock } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { MaskedLines, FadeUp } from "../components/Reveal";
import { api, formatApiErrorDetail } from "../lib/api";

const PERKS = [
    "One year of active dispute filing across all 3 bureaus",
    "Credit worked on every month — new letters, follow-ups & escalations",
    "Collections & charge-off removal (FDCPA + FCRA)",
    "Identity theft resolution & fraud protection",
    "Live client portal to track every letter and score change",
    "The 93-point program guarantee — in writing",
];

export default function Enroll() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", goal: "" });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/payments/enrollment", {
                name: form.name,
                email: form.email,
                phone: form.phone || null,
                goal: form.goal || null,
            });
            toast.success("Redirecting you to secure checkout…");
            // brief delay so the toast is visible
            setTimeout(() => {
                window.location.href = data.checkout_url;
            }, 500);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background text-foreground">
            <Nav />

            <section className="pt-28 md:pt-36 pb-16 grain">
                <div className="mx-auto max-w-[1600px] px-6 md:px-10">
                    <div className="grid grid-cols-12 gap-6 items-end">
                        <div className="col-span-12 md:col-span-8">
                            <div className="flex items-center gap-3 mb-8 text-[11px] tracking-[0.24em] uppercase text-muted-foreground">
                                <span className="w-8 h-px bg-muted-foreground" />
                                <ShieldCheck size={12} strokeWidth={1.5} />
                                <span>Enrollment · Secure checkout by Square</span>
                            </div>
                            <MaskedLines
                                as="h1"
                                className="font-editorial tracking-tighter text-[12vw] md:text-[8.4vw] lg:text-[7.2vw] leading-[0.86]"
                                lines={["Get", <><span className="italic text-accent">started.</span></>]}
                            />
                        </div>
                        <div className="col-span-12 md:col-span-4 md:text-right">
                            <FadeUp delay={0.5}>
                                <p className="text-base text-muted-foreground max-w-sm md:ml-auto leading-relaxed">
                                    One payment. A full year of work on your credit. Every month.
                                </p>
                            </FadeUp>
                        </div>
                    </div>
                </div>
            </section>

            <section className="pb-24">
                <div className="mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-12 gap-6">
                    {/* Perks / plan card */}
                    <div className="col-span-12 lg:col-span-7 bg-card border hairline p-8 md:p-12">
                        <div className="text-[11px] tracking-[0.24em] uppercase text-muted-foreground">
                            The Membership
                        </div>
                        <div className="mt-2 font-editorial text-4xl md:text-5xl tracking-tighter">
                            1-Year Access
                        </div>
                        <p className="mt-4 text-sm text-muted-foreground max-w-lg">
                            One-time enrollment. We work your credit for a full year — filing,
                            escalating, negotiating and rebuilding — every single month.
                        </p>

                        <ul className="mt-10 border-t hairline">
                            {PERKS.map((p, i) => (
                                <li
                                    key={p}
                                    className="grid grid-cols-12 gap-4 border-b hairline py-4 items-baseline"
                                    data-testid={`enroll-perk-${i}`}
                                >
                                    <div className="col-span-1 font-mono-ui text-[10px] tracking-[0.2em] text-muted-foreground">
                                        0{i + 1}
                                    </div>
                                    <div className="col-span-11 text-sm md:text-base">{p}</div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Price card + form */}
                    <aside className="col-span-12 lg:col-span-5 space-y-4">
                        <div className="bg-foreground text-background p-8 md:p-10">
                            <div className="text-[10px] tracking-[0.24em] uppercase text-background/60">
                                One-time enrollment
                            </div>
                            <div className="mt-6 flex items-baseline gap-3">
                                <div className="font-editorial text-[112px] md:text-[128px] leading-[0.86] tracking-tighter">
                                    $500
                                </div>
                                <div className="font-mono-ui text-xs tracking-[0.16em] uppercase text-background/60">
                                    / year
                                </div>
                            </div>
                            <div className="mt-6 flex items-center gap-3 border-t border-background/20 pt-6">
                                <ShieldCheck size={20} strokeWidth={1.4} className="text-accent" />
                                <div className="font-mono-ui text-xs tracking-[0.14em] uppercase text-background/70">
                                    Includes the 93-point guarantee
                                </div>
                            </div>
                        </div>

                        <form
                            onSubmit={submit}
                            className="bg-card border hairline p-6 md:p-8"
                            data-testid="enroll-form"
                        >
                            <div className="text-[10px] tracking-[0.24em] uppercase text-muted-foreground">
                                Your details
                            </div>
                            <div className="mt-4 space-y-4">
                                <input
                                    required
                                    placeholder="Full name"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    data-testid="enroll-name"
                                    className="w-full bg-transparent border-b hairline focus:border-foreground outline-none py-2 font-mono-ui text-sm"
                                />
                                <input
                                    type="email"
                                    required
                                    placeholder="Email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    data-testid="enroll-email"
                                    className="w-full bg-transparent border-b hairline focus:border-foreground outline-none py-2 font-mono-ui text-sm"
                                />
                                <input
                                    placeholder="Phone"
                                    value={form.phone}
                                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                    data-testid="enroll-phone"
                                    className="w-full bg-transparent border-b hairline focus:border-foreground outline-none py-2 font-mono-ui text-sm"
                                />
                                <input
                                    placeholder="Goal (e.g. buy a home) — optional"
                                    value={form.goal}
                                    onChange={(e) => setForm({ ...form, goal: e.target.value })}
                                    data-testid="enroll-goal"
                                    className="w-full bg-transparent border-b hairline focus:border-foreground outline-none py-2 font-mono-ui text-sm"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="enroll-submit"
                                className="mt-8 group w-full inline-flex items-center justify-between bg-foreground text-background px-5 py-4 text-[12px] tracking-[0.18em] uppercase hover:bg-accent disabled:opacity-60"
                            >
                                {loading ? (
                                    <>
                                        <span>Redirecting to Square…</span>
                                        <Loader2 size={16} className="animate-spin" />
                                    </>
                                ) : (
                                    <>
                                        <span>Pay $500 · Enroll now</span>
                                        <ArrowUpRight size={16} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                                    </>
                                )}
                            </button>
                            <p className="mt-5 flex items-center gap-2 font-mono-ui text-[10px] tracking-[0.16em] uppercase text-muted-foreground">
                                <Lock size={12} strokeWidth={1.5} /> Encrypted checkout · Cards & digital wallets accepted
                            </p>
                            <p className="mt-4 text-xs text-muted-foreground">
                                Already enrolled?{" "}
                                <Link to="/login" className="underline underline-offset-4">
                                    Sign in
                                </Link>
                            </p>
                        </form>
                    </aside>
                </div>
            </section>

            <Footer />
        </div>
    );
}
